// Logique pure (sans React ni d3) des frises d'orchestration, partagee par la frise d'un
// environnement et celle d'un serveur : libelles francais, graduations, filtre d'heures
// (axe "casse" : seules les tranches horaires retenues, cote a cote), rangement en sous-lignes,
// simultaneite. Les horodatages de l'API sont naifs (heure locale d'affichage) : aucune
// conversion de fuseau, et la locale globale de moment n'est jamais modifiee.

const p2 = (n) => String(n).padStart(2, "0");
const H = 3600000;

export const JOURS_FR = ["dim.", "lun.", "mar.", "mer.", "jeu.", "ven.", "sam."];
export const ECART_TRANCHES = 6; // px entre deux tranches de l'axe casse
export const LARGEUR_MIN_GRADUATION = 28; // px minimum entre deux graduations horaires
const PAS_POSSIBLES = [1, 2, 3, 6, 12];

// "ven. 30/10"
export const libelleJour = (d) => `${JOURS_FR[d.getDay()]} ${p2(d.getDate())}/${p2(d.getMonth() + 1)}`;
// "14 h" (minuit : "0 h")
export const libelleHeure = (h) => `${h} h`;
// "cycle ven. 30/10 → sam. 31/10" (jour de depart, lendemain)
export const libelleCycle = (d) => {
  const suivant = new Date(d);
  suivant.setDate(suivant.getDate() + 1);
  return `cycle ${libelleJour(d)} → ${libelleJour(suivant)}`;
};
// "30/10 02:15" pour le compteur de pic
export const libelleInstant = (ms) => {
  const d = new Date(ms);
  return `${p2(d.getDate())}/${p2(d.getMonth() + 1)} à ${p2(d.getHours())}:${p2(d.getMinutes())}`;
};

export const ms = (v) => (v === null || v === undefined || v === "" ? NaN : new Date(String(v)).getTime());

// --- Plage d'heures -------------------------------------------------------------------------
// `origine` = heure de frontiere de "jour" de la grille. Decision metier du 30/10/2026 : les jobs
// partent vers 19 h et peuvent deborder le lendemain, la frontiere est donc toujours a 19 h
// (cycle 19 h -> 19 h) ; minuit n'est qu'une graduation ordinaire. Il n'y a plus de
// preregalage "journee entiere" a frontiere de minuit.
export const ORIGINE_CYCLE = 19;
export const PLAGE_CYCLE = { debut: 0, fin: 24, origine: ORIGINE_CYCLE };
export const PLAGE_NUIT = { debut: 19, fin: 8, origine: ORIGINE_CYCLE };
export const PLAGE_JOUR = { debut: 8, fin: 19, origine: ORIGINE_CYCLE };
export const PLAGE_DEFAUT = PLAGE_CYCLE;
export const PRESETS_PLAGE = [
  { key: "cycle", label: "Cycle 19 h → 19 h", plage: PLAGE_CYCLE },
  { key: "nuit", label: "Nuit 19 h–8 h", plage: PLAGE_NUIT },
  { key: "jour", label: "Journée 8 h–19 h", plage: PLAGE_JOUR },
];

const borne = (v) => Math.min(24, Math.max(0, Math.round(Number(v)) || 0));
export const normaliserPlage = (p) => ({
  debut: borne(p && p.debut),
  fin: borne(p && p.fin),
  origine: p && p.origine !== undefined && p.origine !== null ? borne(p.origine) % 24 : ORIGINE_CYCLE,
});
// plage couvrant toute la journee : 0-24, ou debut = fin (8 -> 8)
export const plageComplete = (p) => {
  const { debut, fin } = normaliserPlage(p);
  return debut === fin || (debut === 0 && fin === 24);
};
export const clePlage = (p) => {
  const n = normaliserPlage(p);
  // une plage libre couvrant 24 h (0 -> 24, 0 -> 0, 19 -> 19...) equivaut au cycle
  if (plageComplete(n)) return "cycle";
  const f = PRESETS_PLAGE.find((x) => x.plage.debut === n.debut && x.plage.fin === n.fin);
  return f ? f.key : "perso";
};

// Tranches horaires retenues entre `debut` et `fin` (Date), en ms, triees, rognees a la fenetre.
// `debut > fin` : plage passant minuit (20 -> 8 = de 20 h a 8 h le lendemain).
export function tranchesHoraires(debut, fin, plage) {
  const a = debut instanceof Date ? debut.getTime() : ms(debut);
  const b = fin instanceof Date ? fin.getTime() : ms(fin);
  if (!(b > a)) return [];
  if (plageComplete(plage)) return [{ t0: a, t1: b }];
  const { debut: hd, fin: hf } = normaliserPlage(plage);
  const jour = new Date(a);
  jour.setHours(0, 0, 0, 0);
  jour.setDate(jour.getDate() - 1); // une nuit commencee la veille peut deborder sur le 1er jour
  const out = [];
  for (; jour.getTime() < b; jour.setDate(jour.getDate() + 1)) {
    const at = (h) => {
      const d = new Date(jour);
      d.setHours(h, 0, 0, 0); // h > 23 : bascule sur le lendemain
      return d.getTime();
    };
    const t0 = Math.max(at(hd), a);
    const t1 = Math.min(hd < hf ? at(hf) : at(24 + hf), b);
    if (t1 > t0) out.push({ t0, t1 });
  }
  return out;
}

// Place les tranches sur l'axe : echelle lineaire par morceaux, ecart fixe entre tranches.
export function construireEchelle(tranches, x0, largeur, ecart = ECART_TRANCHES) {
  const total = tranches.reduce((s, t) => s + (t.t1 - t.t0), 0);
  const utile = Math.max(1, largeur - ecart * Math.max(0, tranches.length - 1));
  const px = total > 0 ? utile / total : 0;
  let cur = x0;
  const placees = tranches.map((t) => {
    const x = cur;
    cur += (t.t1 - t.t0) * px + ecart;
    return { ...t, x0: x, x1: x + (t.t1 - t.t0) * px };
  });
  const xDans = (tr, t) => tr.x0 + (Math.min(Math.max(t, tr.t0), tr.t1) - tr.t0) * px;
  return {
    tranches: placees,
    x0,
    x1: x0 + largeur,
    ecart,
    pxParHeure: px * H,
    xDans,
    // x d'un instant : dans une tranche, position lineaire ; dans un trou, bord de la tranche suivante
    x(t) {
      for (const tr of placees) {
        if (t <= tr.t1) return xDans(tr, t);
      }
      return placees.length ? placees[placees.length - 1].x1 : x0;
    },
    // morceaux visibles d'un intervalle [s, e] : un par tranche traversee
    segments(s, e) {
      return decouperIntervalle(placees, s, e).map((m) => ({
        ...m,
        x0: xDans(m.tranche, m.t0),
        x1: xDans(m.tranche, m.t1),
      }));
    },
  };
}

// Morceaux [t0, t1] d'un intervalle dans chaque tranche, avec les indicateurs de rognage.
// Un intervalle de duree nulle est conserve s'il tombe dans une tranche.
export function decouperIntervalle(tranches, s, e) {
  if (isNaN(s)) return [];
  const fin = isNaN(e) || e < s ? s : e;
  const out = [];
  tranches.forEach((tr) => {
    const ok = fin === s ? s >= tr.t0 && s <= tr.t1 : s < tr.t1 && fin > tr.t0;
    if (!ok) return;
    out.push({
      tranche: tr,
      t0: Math.max(s, tr.t0),
      t1: Math.min(fin, tr.t1),
      tronqueDebut: s < tr.t0,
      tronqueFin: fin > tr.t1,
    });
  });
  return out;
}

export const intersecteTranches = (tranches, s, e) => decouperIntervalle(tranches, s, e).length > 0;

// --- Graduations ----------------------------------------------------------------------------
// Pas de base selon la periode (1 j : 1 h, 3 j : 3 h, 7 j : 6 h), releve au multiple superieur
// (1 -> 2 -> 3 -> 6 -> 12) tant qu'une graduation ferait moins de 28 px.
export function pasGraduation(jours, pxParHeure) {
  const base = jours <= 1 ? 1 : jours <= 3 ? 3 : 6;
  let i = PAS_POSSIBLES.indexOf(base);
  while (i < PAS_POSSIBLES.length - 1 && PAS_POSSIBLES[i] * pxParHeure < LARGEUR_MIN_GRADUATION) i += 1;
  return PAS_POSSIBLES[i];
}

// Graduations horaires (heures locales multiples de `pas`, 0 h incluse) et segments de jours.
// `origine` : heure de frontiere de jour (0 = minuit). La frontiere est toujours graduee ; les
// graduations ordinaires trop proches d'elle (moins d'un pas, 4 h au plus) sont omises.
export function graduations(ech, pas, origine = 0) {
  const ticks = [];
  const jours = [];
  ech.tranches.forEach((tr) => {
    const d = new Date(tr.t0);
    d.setMinutes(0, 0, 0);
    if (d.getTime() < tr.t0) d.setHours(d.getHours() + 1);
    for (; d.getTime() <= tr.t1; d.setHours(d.getHours() + 1)) {
      const h = d.getHours();
      const dist = Math.min(Math.abs(h - origine), 24 - Math.abs(h - origine));
      if (h === origine || (h % pas === 0 && (origine === 0 || dist >= Math.min(pas, 4)))) {
        ticks.push({
          t: d.getTime(),
          x: ech.xDans(tr, d.getTime()),
          heure: h,
          minuit: h === 0,
          frontiere: h === origine,
          label: libelleHeure(h),
        });
      }
    }
    let cur = tr.t0;
    while (cur < tr.t1) {
      // prochaine frontiere de jour strictement apres cur
      const nm = new Date(cur);
      nm.setHours(origine, 0, 0, 0);
      if (nm.getTime() <= cur) nm.setDate(nm.getDate() + 1);
      const e = Math.min(nm.getTime(), tr.t1);
      // jour (ou cycle) auquel appartient le segment : celui de la derniere frontiere passee
      const debutJour = new Date(cur);
      debutJour.setHours(debutJour.getHours() - origine);
      jours.push({
        debut: cur,
        fin: e,
        x0: ech.xDans(tr, cur),
        x1: ech.xDans(tr, e),
        label: origine === 0 ? libelleJour(new Date(cur)) : libelleCycle(debutJour),
        court: origine === 0 ? libelleJour(new Date(cur)) : `${libelleJour(debutJour)} ${libelleHeure(origine)}`,
      });
      cur = e;
    }
  });
  return { ticks, jours };
}

// --- Rangement en sous-lignes (remplissage glouton) -----------------------------------------
// Aucune superposition dans une meme sous-ligne ; des bornes qui se touchent partagent la ligne.
// `max` : nombre maximal de sous-lignes ; les runs qui ne tiennent pas sont ranges sur une
// derniere ligne de debordement (index `max`), comptes dans `debordes`.
export function rangerEnLignes(runs, finParDefaut, max = Infinity) {
  const items = (Array.isArray(runs) ? runs : [])
    .map((r) => {
      const s = ms(r.start);
      const e = finEffective(r, finParDefaut);
      return { r, s, e: isNaN(e) || e < s ? s : e };
    })
    .filter((x) => !isNaN(x.s))
    .sort((a, b) => a.s - b.s || a.e - b.e);
  const fins = [];
  const lignes = new Map();
  let debordes = 0;
  items.forEach(({ r, s, e }) => {
    let i = fins.findIndex((f) => f <= s);
    if (i < 0 && fins.length >= max) {
      lignes.set(r.order, max);
      debordes += 1;
      return;
    }
    if (i < 0) {
      i = fins.length;
      fins.push(e);
    } else {
      fins[i] = e;
    }
    lignes.set(r.order, i);
  });
  return { lignes, nb: fins.length + (debordes ? 1 : 0), debordes };
}

// --- Simultaneite ---------------------------------------------------------------------------
// Fin effective d'un run : `end` ; sans `end`, seul un run `running` se prolonge jusqu'a `fin`
// (maintenant / borne) ; un autre run sans fin est de duree nulle (comme cote back).
export const finEffective = (r, fin) => (r.end ? ms(r.end) : r.status === "running" ? fin : ms(r.start));
const finRun = finEffective;

// Escalier du nombre de runs simultanes : [{ t0, t1, n }] (balayage, bornes jointes = pas de
// chevauchement, runs de duree nulle ignores).
export function paliersSimultanes(runs, finParDefaut) {
  const ev = [];
  (Array.isArray(runs) ? runs : []).forEach((r) => {
    const s = ms(r.start);
    const e = finRun(r, finParDefaut);
    if (r.stale || isNaN(s) || isNaN(e) || e <= s) return; // run bloque (stale) : ignore
    ev.push([s, 1], [e, -1]);
  });
  ev.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const paliers = [];
  let n = 0;
  let prev = null;
  ev.forEach(([t, d]) => {
    if (prev !== null && t > prev) paliers.push({ t0: prev, t1: t, n });
    n += d;
    prev = t;
  });
  return paliers;
}

// Pic de simultaneite restreint aux tranches retenues : { count, at } (at en ms, null si aucun).
export function picSimultane(paliers, tranches) {
  let best = { count: 0, at: null };
  paliers.forEach((p) => {
    if (p.n <= best.count) return;
    const m = decouperIntervalle(tranches, p.t0, p.t1)[0];
    if (m) best = { count: p.n, at: m.t0 };
  });
  return best;
}

// Orders des runs qui chevauchent strictement le run `ordre` (lui-meme inclus) ; vide si null.
export function ordresConcurrents(runs, ordre, finParDefaut) {
  if (ordre === null || ordre === undefined) return new Set();
  const liste = Array.isArray(runs) ? runs : [];
  const cible = liste.find((r) => r.order === ordre);
  if (!cible) return new Set();
  const s = ms(cible.start);
  const e = finRun(cible, finParDefaut);
  const res = new Set([ordre]);
  if (cible.stale || !(e > s)) return res; // run bloque ou de duree nulle : pas de concurrents
  liste.forEach((r) => {
    if (r.order === ordre || r.stale || !(finRun(r, finParDefaut) > ms(r.start))) return;
    const rs = ms(r.start);
    const re = finRun(r, finParDefaut);
    if (rs < e && s < re) res.add(r.order);
  });
  return res;
}

// Debut de l'axe de la frise : `from` de la reponse, jamais avant. Si la liste est tronquee,
// `first_start` (debut du plus ancien run conserve) arrondi a l'heure inferieure, s'il est
// posterieur a `from`. Les runs commences avant ce debut sont rognes a gauche.
export function debutAxe(data) {
  if (!data) return null;
  if (data.truncated && data.first_start) {
    const d = new Date(String(data.first_start));
    if (!isNaN(d.getTime())) {
      d.setMinutes(0, 0, 0);
      const f = ms(data.from);
      return isNaN(f) || d.getTime() > f ? d : data.from;
    }
  }
  return data.from;
}

// Palette categorielle cyclique (une couleur par environnement).
export const PALETTE_ENV = [
  "#195c84", "#14a3a3", "#7c5cff", "#d97706", "#2e9e6b", "#c2410c",
  "#0ea5e9", "#a855f7", "#65a30d", "#db2777", "#0f766e", "#6366f1",
];
export const couleurEnv = (index) => PALETTE_ENV[((index % PALETTE_ENV.length) + PALETTE_ENV.length) % PALETTE_ENV.length];
